import { useCallback, useState } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useUpdateSourcingEventMutation, type UpdatePublicSourcingEventRequest } from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";

type SavingFieldKey = keyof UpdatePublicSourcingEventRequest;

export const useSaveField = (eventId: string) => {
  const { triggerSoft } = useApiError();
  const [savingCounts, setSavingCounts] = useState<Map<SavingFieldKey, number>>(new Map());
  const [updateEvent] = useUpdateSourcingEventMutation();

  const saveField = useCallback(
    // Each call should update exactly one field — only the first key is tracked for loading state.
    async (fields: UpdatePublicSourcingEventRequest) => {
      const key = Object.keys(fields)[0] as SavingFieldKey | undefined;
      if (!key) return;
      setSavingCounts((prev) => new Map(prev).set(key, (prev.get(key) ?? 0) + 1));
      try {
        await updateEvent({ eventId, updatePublicSourcingEventRequest: fields }).unwrap();
      } catch (e) {
        triggerSoft(e as FetchBaseQueryError | SerializedError);
      } finally {
        setSavingCounts((prev) => {
          const next = new Map(prev);
          const count = (next.get(key) ?? 1) - 1;
          if (count <= 0) next.delete(key);
          else next.set(key, count);
          return next;
        });
      }
    },
    [eventId, updateEvent, triggerSoft]
  );

  const isSaving = useCallback(
    (...keys: SavingFieldKey[]) => keys.some((k) => (savingCounts.get(k) ?? 0) > 0),
    [savingCounts]
  );

  return { saveField, isSaving };
};
